import type { DeviceInfo } from './hubAlertSetupRules';

/** `navigator.userAgentData` brand versions (Chromium); the user agent string otherwise. */
export const browserVersion = (
  brands: ReadonlyArray<{ brand: string; version?: string }> | null | undefined,
  userAgent: string,
) => {
  const named = (brands ?? []).find(
    entry => entry.brand === 'Google Chrome' || entry.brand === 'Microsoft Edge',
  );
  if (named?.version) return named.version;
  return /(?:Edg|Chrome)\/(\d+)/.exec(userAgent)?.[1] ?? null;
};

export type DeviceReportInput = {
  at: Date;
  user: string;
  facility: string;
  device: DeviceInfo;
  browserVersion: string | null;
  installed: boolean;
  permission: NotificationPermission | 'unsupported';
  connection: string | null;
  testPassedAt: Date | null;
  release: string | null;
  mode: string;
};

const PLATFORM_NAMES: Record<DeviceInfo['platform'], string> = {
  android: 'Android',
  windows: 'Windows',
  mac: 'Mac',
  other: 'Other device',
};
const BROWSER_NAMES: Record<DeviceInfo['browser'], string> = {
  chrome: 'Chrome',
  edge: 'Edge',
  other: 'Other browser',
};
const PERMISSION_NAMES: Record<DeviceReportInput['permission'], string> = {
  granted: 'allowed',
  denied: 'blocked',
  default: 'not asked yet',
  unsupported: 'not supported',
};

const stamp = (date: Date) =>
  date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Plain text an operator pastes into WhatsApp or Slack for support: what this device and Hub
 * report about order alerts, without any secret or token.
 */
export const deviceReport = (input: DeviceReportInput) =>
  [
    'Hub order alerts: device check',
    `Time: ${stamp(input.at)}`,
    `User: ${input.user}`,
    `Facility: ${input.facility}`,
    `Device: ${PLATFORM_NAMES[input.device.platform]} · ${BROWSER_NAMES[input.device.browser]}${
      input.browserVersion ? ` ${input.browserVersion}` : ''
    }`,
    `Hub app: ${input.installed ? 'installed app' : 'browser tab'}`,
    `Notifications: ${PERMISSION_NAMES[input.permission]}`,
    `Connection: ${input.connection ?? 'checking'}`,
    `Sound test: ${input.testPassedAt ? `passed ${stamp(input.testPassedAt)}` : 'not done'}`,
    `Hub version: ${input.release ? input.release.replace(/^warehouse@/, '').slice(0, 7) : 'local'}`,
    `Setup mode: ${input.mode}`,
  ].join('\n');
